import {
  Box,
  HStack,
  Input,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  Button,
} from '@chakra-ui/react';
import { useGlobalStoreContext } from '../../context';
import { graphqlFetcher } from '../../config/axios';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';
import { usePagination } from '../../hooks';
import Loader from '../../components/loader';
import Error from '../../components/error';

const query = (skip: number, limit: number, keyword: string) => `{
  events(skip: ${skip}, limit: ${limit}, keyword: "${keyword}") {
    count
    data {
      id
      name
      host
      score
      category {
        name
      }
      date
      createdAt
    }
  }
}`;

const Events = () => {
  const { dispatch } = useGlobalStoreContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsCount, setEventsCount] = useState(1);
  const navigate = useNavigate();
  const {
    firstContentIndex,
    lastContentIndex,
    nextPage,
    prevPage,
    page,
    setPage,
  } = usePagination({
    count: eventsCount === 0 ? 1 : eventsCount,
    contentPerPage: 20,
  });
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const gottenEvents = await graphqlFetcher(
        query(firstContentIndex, lastContentIndex, keyword)
      );
      setEventsCount(gottenEvents.data.data.events.count);
      setEvents(gottenEvents.data.data.events.data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [firstContentIndex, lastContentIndex, keyword]);

  useEffect(() => {
    (async () => {
      await fetchEvents();
    })();
  }, [fetchEvents]);

  useEffect(() => {
    dispatch({
      type: 'SETUP_PAGE',
      payload: {
        title: 'events',
        description: 'list of all events',
      },
    });
  }, [dispatch]);
  return (
    <Box>
      <HStack justifyContent="center">
        <Input
          w="lg"
          variant="filled"
          placeholder="Search for events"
          onChange={(e) => {
            setPage(1);
            setKeyword(e.target.value);
          }}
          value={keyword}
        />
      </HStack>
      <HStack mt="5" justifyContent="space-between">
        <HStack gap="3">
          <Text color="GrayText">
            <b>
              {firstContentIndex + 1} -{' '}
              {lastContentIndex > eventsCount ? eventsCount : lastContentIndex}
            </b>{' '}
            of {eventsCount}
          </Text>
          <HStack>
            <IconButton
              aria-label="prev page"
              onClick={prevPage}
              icon={<span>{'<'}</span>}
              size="sm"
              fontWeight="bold"
              disabled={page === 1}
            />
            <IconButton
              aria-label="next page"
              onClick={nextPage}
              icon={<span>{'>'}</span>}
              size="sm"
              fontWeight="bold"
              disabled={lastContentIndex >= eventsCount}
            />
          </HStack>
        </HStack>
        <Button size="sm" variant="gradient" as={Link} to="create">
          create event
        </Button>
      </HStack>
      {loading ? (
        <Loader />
      ) : error ? (
        <Error />
      ) : (
        <Box w="100%" overflow="scroll">
          <Table
            overflowX="auto"
            whiteSpace="nowrap"
            mt="6"
            variant="simple"
            colorScheme="gray"
          >
            <Thead>
              <Tr>
                <Th></Th>
                <Th>name</Th>
                <Th>host</Th>
                <Th>score</Th>
                <Th>date</Th>
                <Th>created at</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((el, index) => (
                <Tr
                  key={el.id}
                  cursor="pointer"
                  _hover={{ backgroundColor: 'background.100' }}
                  onClick={() => navigate(el.id)}
                >
                  <Td textTransform="capitalize">
                    {index + firstContentIndex + 1}
                  </Td>
                  <Td textTransform="capitalize">{el.name}</Td>
                  <Td textTransform="capitalize">{el.host}</Td>
                  <Td color="green.500" fontWeight="bold">
                    {el.score}
                  </Td>
                  <Td>{dayjs(el.date).format('MMM D, YYYY h:mm A')}</Td>
                  <Td>{dayjs(el.createdAt).format('MMM D, YYYY h:mm A')}</Td>
                </Tr>
              ))}
            </Tbody>
            <Tfoot>
              <Tr>
                <Th></Th>
                <Th>name</Th>
                <Th>host</Th>
                <Th>score</Th>
                <Th>date</Th>
                <Th>created at</Th>
              </Tr>
            </Tfoot>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default Events;
